import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
    FaPlayCircle,
    FaBook,
    FaInfoCircle,
    FaSearch,
    FaFilter,
    FaRegCalendarCheck,
    FaArrowRight,
    FaTimes,
    FaDownload,
    FaFileAlt,
    FaVideo,
    FaFileVideo,
    FaLink,
    FaFileArchive
} from 'react-icons/fa';
import { showError } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import EmptyState from '../../shared/EmptyState';
import { Link } from 'react-router-dom';
import { API_URL } from '../../../config';
import './MyCourses.css';

const MyCourses = () => {
    const [enrollments, setEnrollments] = useState([]);
    const [liveSessions, setLiveSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sessionsLoading, setSessionsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [activeTab, setActiveTab] = useState('courses');
    const [selectedSession, setSelectedSession] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState('All'); // All, Active, Completed

    const fetchMyCourses = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/student/my-courses', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEnrollments(res.data);
            setLoading(false);
        } catch (err) {
            showError('Error', 'Failed to fetch enrolled courses');
            setLoading(false);
        }
    };

    const fetchLiveSessions = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/student/live-sessions', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLiveSessions(res.data);
            setSessionsLoading(false);
        } catch (err) {
            console.error(err);
            setSessionsLoading(false);
        }
    };

    const openSessionModal = (session) => {
        setSelectedSession(session);
        setIsModalOpen(true);
    };

    const closeSessionModal = () => {
        setIsModalOpen(false);
        setTimeout(() => setSelectedSession(null), 300);
    };

    const handleJoinSession = async (session) => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`/api/student/live-classes/${session._id}/join`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            window.open(res.data.externalMeetingUrl, '_blank');
            fetchLiveSessions(); // Refresh to update potentially changed status
        } catch (err) {
            console.error("Join session error:", err);
            window.open(session.externalMeetingUrl, '_blank');
        }
    };

    useEffect(() => {
        fetchMyCourses();
        fetchLiveSessions();
    }, []);

    if (loading) return <Loader />;

    // Derive unique categories from enrolled courses
    const categories = ['All', ...new Set(enrollments.map(e => e.courseId?.category).filter(Boolean))];

    // Filter Logic
    const filteredEnrollments = enrollments.filter(enrollment => {
        const course = enrollment.courseId;
        if (!course) return false;

        const matchesSearch = course.title?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'All' || course.category === selectedCategory;
        const matchesStatus = statusFilter === 'All' || enrollment.status === statusFilter;

        return matchesSearch && matchesCategory && matchesStatus;
    });

    return (
        <div className="my-courses fade-in">
            <div className="dashboard-header">
                <div>
                    <h1 className="page-title">My Learning Journeys</h1>
                    <p className="page-subtitle">Track your progress and continue where you left off in your skill-building path.</p>
                </div>
            </div>

            <div className="learning-tabs-container">
                <button
                    className={`learning-tab ${activeTab === 'courses' ? 'active' : ''}`}
                    onClick={() => setActiveTab('courses')}
                >
                    <FaBook className="inline-icon" /> Enrolled Courses
                </button>
                <button
                    className={`learning-tab ${activeTab === 'live-sessions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('live-sessions')}
                >
                    <FaPlayCircle className="inline-icon" /> Live Sessions
                </button>
            </div>

            {/* --- COURSES TAB --- */}
            {activeTab === 'courses' && (
                <>
                    {/* --- Advanced Filters Section --- */}
                    {enrollments.length > 0 && (
                        <div className="modern-filters-bar">
                            <div className="search-wrapper">
                                <FaSearch className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search your courses..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="filter-actions">
                                <div className="category-select-wrapper">
                                    <FaFilter className="filter-icon" />
                                    <select
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                    >
                                        {categories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="category-select-wrapper">
                                    <FaRegCalendarCheck className="filter-icon" />
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="All">Status: All</option>
                                        <option value="Active">In Progress</option>
                                        <option value="Completed">Completed</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {enrollments.length > 0 ? (
                        filteredEnrollments.length > 0 ? (
                            <div className="enrolled-grid">
                                {filteredEnrollments.map(enrollment => {
                                    const course = enrollment.courseId;
                                    const progress = enrollment.progress || 0;

                                    return (
                                        <div key={enrollment._id} className="enrolled-card">
                                            <div className="enrolled-thumb">
                                                {course.thumbnailUrl ? (
                                                    <img src={`${API_URL}${course.thumbnailUrl}`} alt={course.title} />
                                                ) : (
                                                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#002C5F' }}>
                                                        <FaBook size={48} color="white" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="enrolled-content">
                                                <h3 className="enrolled-title">{course.title}</h3>
                                                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                                                    {course.category || 'General'} • {course.difficultyLevel || 'Beginner'}
                                                </p>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748B' }}>
                                                    <FaInfoCircle /> {course.modules?.length || 0} Modules Total
                                                </div>
                                            </div>
                                            <div className="card-actions">
                                                {enrollment.status === 'Completed' ? (
                                                    <Link to={`/student-dashboard/learning/${course._id}`} className="action-link completed">
                                                        Course Completed <FaRegCalendarCheck />
                                                    </Link>
                                                ) : (
                                                    <Link to={`/student-dashboard/learning/${course._id}`} className="action-link">
                                                        {progress > 0 ? 'Resume Learning' : 'Start Course'} <FaArrowRight />
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <EmptyState
                                icon={FaBook}
                                title="No courses found"
                                message="We couldn't find any courses matching your specific filters. Try clearing them or searching for something else."
                                actionText="Clear All Filters"
                                onAction={() => { setSearchTerm(''); setSelectedCategory('All'); setStatusFilter('All'); }}
                            />
                        )
                    ) : (
                        <div style={{ textAlign: 'center', padding: '100px', background: 'white', border: '1px solid var(--border-color)', borderRadius: '20px' }}>
                            <FaPlayCircle size={64} color="#CBD5E1" />
                            <h2 style={{ marginTop: '24px', color: 'var(--primary-blue)' }}>Ready to Upskill?</h2>
                            <p style={{ color: 'var(--text-muted)', marginBottom: '32px' }}>You haven't enrolled in any courses yet. Explore our catalog to get started.</p>
                            <Link to="/student-dashboard/catalog" className="primary-btn" style={{ textDecoration: 'none', display: 'inline-flex' }}>
                                Browse Course Catalog
                            </Link>
                        </div>
                    )}
                </>
            )}

            {/* --- MASTERPIECE LIVE SESSIONS VIEW --- */}
            {activeTab === 'live-sessions' && (
                <div className="live-sessions-master-container fade-in">
                    {sessionsLoading ? <Loader /> : (
                        liveSessions.length > 0 ? (
                            <div className="sessions-card-grid">
                                {liveSessions.map(session => {
                                    const isUpcoming = session.status === 'Scheduled';
                                    const isLive = session.status === 'Live';
                                    const sessionDate = new Date(session.scheduledDate);

                                    return (
                                        <div
                                            key={session._id}
                                            className={`session-master-card clickable ${isLive ? 'live-border' : ''}`}
                                            onClick={() => openSessionModal(session)}
                                        >
                                            {isLive && <div className="live-indicator-tag">LIVE NOW</div>}

                                            <div className="session-card-header">
                                                <div className="course-mini-title">{session.courseId?.title || 'Course Session'}</div>
                                                <h3 className="session-topic">{session.topic}</h3>
                                            </div>

                                            <div className="session-card-body">
                                                <div className="session-meta-item">
                                                    <div className="meta-icon-hex"><FaRegCalendarCheck /></div>
                                                    <div className="meta-text">
                                                        <span>{sessionDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                                        <small>{session.startTime} ({session.duration} mins)</small>
                                                    </div>
                                                </div>

                                                <div className="tutor-mini-profile">
                                                    <div className="tutor-avatar-sm">
                                                        {session.tutorId?.profileImage ? (
                                                            <img src={`${API_URL}${session.tutorId.profileImage}`} alt={session.tutorId.name} />
                                                        ) : (
                                                            <span>{session.tutorId?.name?.charAt(0)}</span>
                                                        )}
                                                    </div>
                                                    <div className="tutor-name-sm">
                                                        <small>Expert Instructor</small>
                                                        <span>{session.tutorId?.name}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="session-card-footer">
                                                <div className={`status-pill-modern ${session.status.toLowerCase()}`}>
                                                    {session.status}
                                                </div>
                                                {(isUpcoming || isLive) ? (
                                                    <span className="join-session-btn" onClick={(e) => { e.stopPropagation(); handleJoinSession(session); }}>
                                                        Join Room <FaArrowRight />
                                                    </span>
                                                ) : (
                                                    <button className="recorded-btn" disabled>Details <FaInfoCircle /></button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="empty-sessions-placeholder">
                                <div className="placeholder-art">
                                    <FaPlayCircle size={80} />
                                </div>
                                <h3>No Live Sessions Scheduled</h3>
                                <p>Stay tuned for upcoming live interactions with your mentors.</p>
                            </div>
                        )
                    )}
                </div>
            )}

            {/* --- SESSION DETAILS MODAL --- */}
            {isModalOpen && selectedSession && createPortal(
                <div className="session-modal-overlay" onClick={closeSessionModal}>
                    <div className="session-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-hero">
                            <div className="modal-topic-tag">{selectedSession.courseId?.title || 'Course Session'}</div>
                            <h2 className="modal-session-title">{selectedSession.topic}</h2>
                        </div>

                        <div className="modal-body-scroll">
                            <div className="session-info-section">
                                <h4 className="section-title-sm">Session Logistics</h4>
                                <div className="info-grid-modern">
                                    <div className="info-item-box">
                                        <label>Date</label>
                                        <span>{new Date(selectedSession.scheduledDate).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                                    </div>
                                    <div className="info-item-box">
                                        <label>Time & Duration</label>
                                        <span>{selectedSession.startTime} ({selectedSession.duration}m)</span>
                                    </div>
                                    <div className="info-item-box">
                                        <label>Instructor</label>
                                        <span>{selectedSession.tutorId?.name || 'Assigned Tutor'}</span>
                                    </div>
                                    <div className="info-item-box">
                                        <label>Current Status</label>
                                        <span style={{ color: selectedSession.status === 'Live' ? '#ef4444' : '#0f172a' }}>
                                            {selectedSession.status}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="session-info-section">
                                <h4 className="section-title-sm">About this Session</h4>
                                <div className="session-description-box">
                                    {selectedSession.description || 'No additional description provided for this session.'}
                                </div>
                            </div>

                            {/* MEETING ARCHIVES SECTION */}
                            {(selectedSession.status === 'Completed' || selectedSession.recordedVideoUrl || (selectedSession.materials && selectedSession.materials.length > 0)) && (
                                <div className="session-info-section">
                                    <h4 className="section-title-sm">Meeting Archives</h4>
                                    <div className="archive-section">
                                        {/* RECORDED VIDEO */}
                                        {selectedSession.recordedVideoUrl && (
                                            <div className="archive-card">
                                                <div className="archive-info">
                                                    <div className="archive-icon-box video">
                                                        <FaFileVideo />
                                                    </div>
                                                    <div className="archive-details">
                                                        <h5>Session Recording</h5>
                                                        <span>Full video replay of the live interactive session.</span>
                                                    </div>
                                                </div>
                                                <a
                                                    href={selectedSession.recordedVideoUrl.startsWith('http') ? selectedSession.recordedVideoUrl : `${API_URL}${selectedSession.recordedVideoUrl}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="archive-action-btn"
                                                >
                                                    Watch Replay
                                                </a>
                                            </div>
                                        )}

                                        {/* SHARED MATERIALS */}
                                        {selectedSession.materials && selectedSession.materials.map((mat, idx) => (
                                            <div key={idx} className="archive-card">
                                                <div className="archive-info">
                                                    <div className="archive-icon-box">
                                                        <FaLink />
                                                    </div>
                                                    <div className="archive-details">
                                                        <h5>{mat.title || 'Shared Resource'}</h5>
                                                        <span>Supplementary learning material shared by the instructor.</span>
                                                    </div>
                                                </div>
                                                <a
                                                    href={mat.url.startsWith('http') ? mat.url : `${API_URL}${mat.url}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="archive-action-btn"
                                                >
                                                    View Resource
                                                </a>
                                            </div>
                                        ))}

                                        {/* LEGACY MATERIAL FIELD (RECORDED DOCUMENT) */}
                                        {(selectedSession.material || selectedSession.recordedDocument) && (
                                            <div className="archive-card">
                                                <div className="archive-info">
                                                    <div className="archive-icon-box">
                                                        <FaFileAlt />
                                                    </div>
                                                    <div className="archive-details">
                                                        <h5>Session Handout</h5>
                                                        <span>PDF/Document resources for this session.</span>
                                                    </div>
                                                </div>
                                                <a
                                                    href={`${API_URL}${selectedSession.material || selectedSession.recordedDocument}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="archive-action-btn"
                                                >
                                                    Download PDF
                                                </a>
                                            </div>
                                        )}

                                        {!selectedSession.recordedVideoUrl && (!selectedSession.materials || selectedSession.materials.length === 0) && !selectedSession.material && !selectedSession.recordedDocument && (
                                            <p style={{ color: '#94a3b8', fontSize: '14px', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                                                Archives will be available once the instructor uploads the session recordings and materials.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer-actions">
                            <button className="modal-secondary-btn" onClick={closeSessionModal}>Dismiss</button>
                            {(selectedSession.status === 'Live' || selectedSession.status === 'Scheduled') && (
                                <button className="modal-primary-btn" onClick={() => handleJoinSession(selectedSession)}>
                                    Enter Meeting Room <FaVideo />
                                </button>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default MyCourses;

